import { VideoContent } from 'components/discover/publications/video-browser/content-pane/video-content';
import { VideoList } from 'components/discover/publications/video-browser/footer/video-list';
import { InitialSelection } from 'components/discover/publications/video-browser/hooks/use-default-selection';
import { useSelection } from 'components/discover/publications/video-browser/hooks/use-selection';
import { CategoryBrowser } from 'components/discover/publications/video-browser/left-pane/category-browser';
import { useToggle } from 'hooks/useToggle';
import { logAnalytics } from 'shared/middlewares/analytics/logger';

import { CategoryNode, CategoryNodes, KeywordNode } from './model';
import { VideoBrowserTemplate } from './template/video-browser-template';

type VideoBrowserPresentationProps = Readonly<{
  tree: CategoryNodes;
  defaultSelection: InitialSelection;
  onClose: () => void;
}>;

export function VideoBrowserPresentation({ onClose, tree, defaultSelection }: VideoBrowserPresentationProps) {
  const {
    activeLabels,
    videoList,
    activeVideoItem,
    activeCategory,
    activeKeyword,
    selectCategory,
    selectCategoryAndKeyword,
    setActiveVideoId,
  } = useSelection(tree, defaultSelection);

  const {
    isOn: isPlaying,
    toggleOn: startPlaying,
    toggleOff: stopPlaying,
    toggle: togglePlaying,
  } = useToggle(defaultSelection.autoPlay);

  const selectThumbnailInVideoList = (videoId: string, autoPlay: boolean) => {
    if (activeVideoItem?.videoId !== videoId) {
      setActiveVideoId(videoId);

      if (autoPlay) {
        logAnalytics.trackEvent('Discover', 'See more videos - Play from thumbnail in video list');
        startPlaying();
      } else {
        stopPlaying();
      }
    } else {
      togglePlaying();
    }
  };

  const playVideoInPreview = (videoTitle: string) => {
    logAnalytics.trackEvent('Discover', 'See more videos - Play from preview', videoTitle);
    startPlaying();
  };

  const onSelectCategory = (cat: CategoryNode) => {
    selectCategory(cat);
    stopPlaying();
  };

  const onSelectCategoryAndKeyword = (cat: CategoryNode, kw: KeywordNode) => {
    selectCategoryAndKeyword(cat, kw);
    stopPlaying();
  };

  return (
    <VideoBrowserTemplate
      leftPane={
        <CategoryBrowser
          tree={tree}
          activeCategory={activeCategory}
          activeKeyword={activeKeyword}
          selectCategory={onSelectCategory}
          selectCategoryAndKeyword={onSelectCategoryAndKeyword}
        />
      }
      onClose={onClose}
    >
      {activeVideoItem && (
        <VideoContent
          activeItem={activeVideoItem}
          activeLabels={activeLabels}
          isPlaying={isPlaying}
          startPlaying={playVideoInPreview}
        />
      )}
      <VideoList
        activeItems={videoList}
        activeVideo={activeVideoItem}
        isPlaying={isPlaying}
        onSelectVideo={selectThumbnailInVideoList}
      />
    </VideoBrowserTemplate>
  );
}
